import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { recordIdentifierFor } from '@warp-drive/core';
import { trackedFunction } from 'reactiveweb/function';

import ContentItems from 'umg-ember-backend/components/page-builder/content-items/index';
import MainSettings from 'umg-ember-backend/components/page-builder/main-settings/index';
import Options from 'umg-ember-backend/components/page-builder/options/index';
import Pages from 'umg-ember-backend/components/page-builder/pages/index';
import Settings from 'umg-ember-backend/components/page-builder/settings/index';
import Component from 'umg-ember-backend/components/record-detail/forms';
import ContentWithSidebar from 'umg-ember-backend/components/ui/content-with-sidebar/index';
import Form from 'umg-ember-backend/components/ui/form';
import storeOnRoute from 'umg-ember-backend/utils/decorators/store-on-route';

export default class PageBuilderComponent extends Component {
  // Services
  @service breadcrumb;
  @service pageBuilderFrontendApi;
  @service permission;
  @service recordStatus;

  // Defaults
  blockName = 'c-page-builder';
  @tracked isMlpSelected = true;
  @storeOnRoute('routeStorageKey') scrollPositionOptions;
  @storeOnRoute('routeStorageKey') selectedContentItem;
  @storeOnRoute('routeStorageKey') _selectedPage;

  get routeStorageKey() {
    return recordIdentifierFor(this.args.model).lid;
  }

  get pageConfig() {
    return this.themeConfig?.cms?.['mlp-page'];
  }

  get themeConfig() {
    return this.loadThemeConfig.value;
  }

  loadLegalContext = trackedFunction(this, async () => {
    const ownedByChannel = await this.args.model.ownedByChannel;
    const legalContext = await ownedByChannel?.legalContext;

    return legalContext?.id ?? '1';
  });

  loadThemeConfig = trackedFunction(this, async () => {
    return await this.pageBuilderFrontendApi.loadThemeConfig(
      this.pageBuilderFrontendApi.host,
      this.args.model,
    );
  });

  // Getter and setter
  get selectedPage() {
    return this._selectedPage ?? this.args.model.pages?.[0];
  }

  get selectedElement() {
    if (this.selectedContentItem?.isDeleted) {
      return this.selectedPage;
    }

    return this.selectedContentItem ?? this.selectedPage;
  }

  get selectedModelToStyle() {
    return this.isMlpSelected ? this.args.model : this.selectedElement;
  }

  get isDisabled() {
    return this.recordStatus.isDisabled(this.args.model);
  }

  // Functions
  editOptions = (model) => {
    this._selectedModelToStyle = model;
  };

  selectPage = (page) => {
    this.selectedContentItem = null;
    this.isMlpSelected = false;
    this._selectedPage = page;
  };

  selectContentItem = (contentItem) => {
    this.isMlpSelected = false;
    this.selectedContentItem = contentItem;
  };

  // Template
  <template>
    <ContentWithSidebar @stickySidebarOffset='40px'>
      <:content>
        <Form @model={{@model}} @showErrors={{@showErrors}} as |form|>
          <MainSettings
            @onEditOptions={{fn this.editOptions @model}}
            @model={{@model}}
            @publish={{fn @onPublishRecord @model}}
            @form={{form}}
          />

          {{#unless form.model.hasInvalidMainSettings}}
            <Pages
              @isDisabled={{this.isDisabled}}
              @mlp={{@model}}
              @onSaveMlp={{@onSaveRecord}}
              @onSelectPage={{this.selectPage}}
              @selectedPage={{this.selectedPage}}
              @showErrors={{@showErrors}}
            />

            {{#if this.selectedPage}}
              <ContentItems
                @contentItems={{this.selectedPage.contentItems}}
                @contentItemTypeOptions={{this.pageConfig.contentItems}}
                @isDisabled={{this.isDisabled}}
                @loadLegalContext={{this.loadLegalContext}}
                @mlp={{@model}}
                @onSaveMlp={{@onSaveRecord}}
                @onSelectContentItem={{this.selectContentItem}}
                @parent={{this.selectedPage}}
                @selectedContentItem={{this.selectedContentItem}}
                @showErrors={{@showErrors}}
              />
            {{/if}}

            <Settings @form={{form}} @model={{form.model}} />
          {{/unless}}
        </Form>
      </:content>
      <:sidebar>
        <Options
          @loadLegalContext={{this.loadLegalContext}}
          @selectedModelToStyle={{this.selectedModelToStyle}}
          @isMlpSelected={{this.isMlpSelected}}
          @mlp={{@model}}
          @showErrors={{@showErrors}}
        />
      </:sidebar>
    </ContentWithSidebar>
  </template>
}
